import { useGetRaffleWinnerQuery, useGetRaffleStatusPublicQuery, useGetSoldTicketsQuery, useLazyGetRaffleWinnerQuery, useRegularizeSoldTicketsMutation, useGetPurchaseOrdersQuery } from "../../store/api";
import { Alert, Box, Button, Chip, Dialog, FormControlLabel, Grid, LinearProgress, Paper, Switch, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { getPrimaryColorByAccountId, getThemeByAccountId, styleConstants } from "../../utils/styleConstants";
import { enqueueSnackbar } from "notistack";
import { Email, EmojiEvents, Person, Phone } from "@mui/icons-material";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { is } from "date-fns/locale";
import { useIsMobileView } from "../../utils/responsive";
import { isTalonario } from "../../utils/raffleType";
import { getContrastTextColor } from "../../utils/color";
import { IPurchaseOrder } from "../../store/interfaces/IPurchaseOrder";
import { TalonarioSaleDialog, TalonarioSaleMode } from "../talonario/TalonarioSaleDialog";
import { TalonarioImageDialog } from "../talonario/TalonarioImageDialog";
import { getPhonePrefix } from "../../constants/phonePrefixes";
import { buildSelfVerificationUrl, buildTicketsWhatsappMessage, buildWhatsappUrl, toWhatsappPhone } from "../../utils/whatsapp";
import { Image as ImageIcon } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

type TalonarioTicketState = 'apartado' | 'pagado';

const RESERVED_STATUSES = ['INITIALIZED', 'TRANSFER_PAYMENT_CREATED', 'CASH_PAYMENT_APPROVED', 'PAYMENT_SUCCESSFUL', 'PROCESS_INITIALIZED'];
const PAID_STATUSES = ['PROCESS_FINISHED', 'SENDING_MAIL_ERROR'];
const SELECTED_COLOR = '#2E7D32';
const RESERVED_COLOR = '#FB8C00';
const EMPTY_TICKETS: number[] = [];

// Estado de cada número tomado según su orden de compra (las rechazadas/canceladas liberan el número)
const buildTicketStates = (purchaseOrders: IPurchaseOrder[] | undefined) => {
    const states = new Map<number, { state: TalonarioTicketState; email: string }>();
    purchaseOrders?.forEach((order) => {
        const state = PAID_STATUSES.includes(order.status) ? 'pagado' : RESERVED_STATUSES.includes(order.status) ? 'apartado' : undefined;
        if (!state) return;
        order.selectedTickets?.forEach((ticket) => states.set(ticket, { state, email: order.purchaser.email }));
    });
    return states;
};

interface SoldTicketsDashboardProps {
    raffleId: string;
    limit: number; // Make limit optional
    raffleType?: string;
    raffleName?: string;
}

export const SoldTicketsDashboard = ({ raffleId, limit, raffleType, raffleName = '' }: SoldTicketsDashboardProps) => {
    const pageSize = limit === 10000 || limit === 100000 ? 1000 : limit;
    const [currentPage, setCurrentPage] = useState(1);
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [getWinnerByTicket, {data, error, isLoading, isFetching}] = useLazyGetRaffleWinnerQuery();
    const [isLoadingGetWinner, setIsLoadingGetWinner] = useState<boolean>(false);
    const [openDialogWinner, setOpenDialogWinner] = useState<boolean>(false);
    const [winner, setWinner] = useState<any | undefined>({});
    const accountId = useAppSelector(
        ({ account }: RootState) => account.id,
      );
    const account = useAppSelector(({ account }: RootState) => account);
    const isMobile = useIsMobileView();

    const talonario = isTalonario(raffleType);
    const [selectedTickets, setSelectedTickets] = useState<number[]>([]);
    const [saleMode, setSaleMode] = useState<TalonarioSaleMode | undefined>();
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [winnerTicket, setWinnerTicket] = useState<number | undefined>();

    const { data: purchaseOrders, refetch: refetchPurchaseOrders } = useGetPurchaseOrdersQuery(raffleId, {
        skip: !talonario || raffleId === 'default',
    });
    const ticketStates = useMemo(() => buildTicketStates(purchaseOrders), [purchaseOrders]);

    const formatTicket = (ticket: number) => ticket.toString().padStart(limit.toString().length - 1, '0');

    const toggleSelectedTicket = (ticket: number) => {
        setSelectedTickets((current) =>
            current.includes(ticket) ? current.filter((t) => t !== ticket) : [...current, ticket].sort((a, b) => a - b),
        );
    };

    const handleCloseSaleDialog = (completed: boolean) => {
        setSaleMode(undefined);
        if (completed) {
            setSelectedTickets([]);
            refetchSoldTickets();
            refetchPurchaseOrders();
            refetchRaffleStatus();
        }
    };

    const handleSendWinnerWhatsapp = () => {
        if (winnerTicket === undefined || !winner?.email) return;
        const supportWhatsapp = account.socialMediaList.find(sm => sm.name === 'supportWhatsapp')?.username;
        const purchaserTickets = Array.from(ticketStates.entries())
            .filter(([, info]) => info.email === winner.email)
            .map(([ticket]) => ticket)
            .sort((a, b) => a - b);
        const message = buildTicketsWhatsappMessage({
            purchaserName: winner.name,
            raffleName,
            tickets: (purchaserTickets.length ? purchaserTickets : [winnerTicket]).map(formatTicket),
            isPaid: ticketStates.get(winnerTicket)?.state !== 'apartado',
            link: buildSelfVerificationUrl(account.url, winner.email, raffleId),
        });
        window.open(buildWhatsappUrl(toWhatsappPhone(winner.phone ?? '', getPhonePrefix(supportWhatsapp)), message), '_blank');
    };

    const handleCloseWinnerDialog = () => {
        setOpenDialogWinner(false);
    };

    const handleToggleShowOnlyAvailable = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnlyAvailable(event.target.checked);
        setCurrentPage(1); // Reset to first page when toggling
    };

    const handleRegularizeSoldTickets = async () => {
        try {
            await regularizeSoldTickets(raffleId).unwrap();
            enqueueSnackbar("Tickets vendidos regularizados exitosamente!", {
                variant: "success",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "top",
                },
            });
            refetchSoldTickets();
            refetchRaffleStatus();
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : "Error desconocido";
            enqueueSnackbar(`Error al regularizar los tickets vendidos: ${errorMessage}`, {
                variant: "error",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "top",
                },
            });
        }
    };

    const handleShowWinner = (selectedNumber: number, isInSoldTickets: boolean | undefined) => {
        if (!isInSoldTickets) {
            enqueueSnackbar(`El ticket ${selectedNumber.toString().padStart(limit.toString().length - 1, '0')} no se ha vendido!`, {
                variant: "info",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "top",
                },
            });
        } else {
            setIsLoadingGetWinner(true);
            setWinnerTicket(selectedNumber);
            getWinnerByTicket({
                raffleId,
                ticket: selectedNumber
            }).then(({data}) => {
                if (data) {
                    setWinner(data);
                    setIsLoadingGetWinner(false);
                    setOpenDialogWinner(true);
                }
            });
        }
    };

    const { data: getSoldTicketsResponse, refetch: refetchSoldTickets } = useGetSoldTicketsQuery(raffleId);
    const [regularizeSoldTickets, { isLoading: isRegularizing }] = useRegularizeSoldTicketsMutation();
    
    const { data: raffleStatusFounded, refetch: refetchRaffleStatus } = useGetRaffleStatusPublicQuery(raffleId, {
        skip: raffleId === 'default',
        refetchOnReconnect: true,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
    });

    // Calcular tickets vendidos
    const soldTicketsCount = getSoldTicketsResponse?.length || 0;
    const progress = raffleStatusFounded?.progress ?? 0;
    // Convertir progress (porcentaje) a número absoluto de tickets
    const progressTicketsCount = limit > 0 ? Math.round((progress / 100) * limit) : 0;
    
    // Calcular porcentaje real de tickets vendidos
    const soldPercentage = limit > 0 ? (soldTicketsCount / limit) * 100 : 0;
    
    // Habilitar botón si hay diferencia entre progress y soldTicketsCount
    const hasDifference = Math.abs(soldTicketsCount - progressTicketsCount) > 0;
    const canRegularize = hasDifference;
    const showRegularizationPanel = soldPercentage > 90 && accountId !== 'oscar-alarcon-promo';

    const colorByAccountId = getPrimaryColorByAccountId(accountId) === '#FFFFFF' ? 'blue': getPrimaryColorByAccountId(accountId);

    const renderTalonarioTicket = (number: number) => {
        const isTaken = !!getSoldTicketsResponse?.includes(number);
        const isSelected = selectedTickets.includes(number);
        // Un número tomado sin orden asociada se considera pagado
        const state = isTaken ? ticketStates.get(number)?.state ?? 'pagado' : undefined;
        const background = isSelected ? SELECTED_COLOR : state === 'pagado' ? colorByAccountId : undefined;
        const borderColor = state === 'apartado' ? RESERVED_COLOR : background;
        const defaultTextColor = getThemeByAccountId(accountId) === 'dark' ? 'white' : '#000';
        const textColor = background ? getContrastTextColor(background) : state === 'apartado' ? RESERVED_COLOR : defaultTextColor;

        return (
            <Paper
                sx={{ m: 1, p: 1, width: 60, textAlign: "center", cursor: 'pointer', bgcolor: background, border: borderColor ? `2px solid ${borderColor}` : 'none' }}
                onClick={() => (isTaken ? handleShowWinner(number, true) : toggleSelectedTicket(number))}
            >
                <Typography textAlign={"center"} variant="body1" color={textColor} fontWeight={isSelected || state ? 'bold' : undefined}>
                    {formatTicket(number)}
                </Typography>
            </Paper>
        );
    };

    const renderTicketNumber = (number: number) => {
        if (talonario) {
            return renderTalonarioTicket(number);
        }
        const isInSoldTickets = getSoldTicketsResponse && getSoldTicketsResponse.includes(number);
        const color = isInSoldTickets ? colorByAccountId : getThemeByAccountId(accountId) === 'dark' ? "white" : "#000";
        return (
            <Paper sx={{ m: 1, p: 1, border: isInSoldTickets ? `1px solid ${colorByAccountId}` : 'none', width: 60, textAlign: "center", }} onClick={() => handleShowWinner(number, isInSoldTickets)}>
                <Typography textAlign={"center"} variant="body1" color={color} sx={isInSoldTickets ? { cursor: 'pointer' } : {}}>
                    {number.toString().padStart(limit.toString().length - 1, '0')}
                </Typography>
            </Paper>
        );
    };

    

    const getAvailableTickets = () => {
        if (!getSoldTicketsResponse) return [];
        const availableTickets = [];
        for (let i = 0; i < limit; i++) {
            const isInSoldTickets = getSoldTicketsResponse.includes(i);
            if (!isInSoldTickets) {
                availableTickets.push(i);
            }
        }
        return availableTickets;
    };

    const getAllTickets = () => {
        const allTickets = [];
        for (let i = 0; i < limit; i++) {
            allTickets.push(i);
        }
        return allTickets;
    };

    const ticketsToShow = showOnlyAvailable ? getAvailableTickets() : getAllTickets();
    const totalPages = Math.ceil(ticketsToShow.length / pageSize);

    const renderTickets = () => {
        const ticketNumbers = [];
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, ticketsToShow.length);
        
        for (let i = startIndex; i < endIndex; i++) {
            const ticketNumber = ticketsToShow[i];
            ticketNumbers.push(
                <Grid item key={ticketNumber}>
                    {renderTicketNumber(ticketNumber)}
                </Grid>
            );
        }

        return ticketNumbers;
    };

    return (
        <Grid container spacing={1} mb={10}>
            {showRegularizationPanel && (
                <Grid item xs={12}>
                    <Alert 
                        severity={hasDifference ? "warning" : "success"}
                        sx={{ mb: 2 }}
                    >
                        <Box>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                Comparación de Tickets Vendidos
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1} mt={1}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">
                                        Progreso reportado ({progress}%):
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {progressTicketsCount} tickets
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">
                                        Tickets vendidos reales:
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {soldTicketsCount} tickets
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop="1px solid rgba(0,0,0,0.1)">
                                    <Typography variant="body2" fontWeight="bold">
                                        Diferencia:
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        fontWeight="bold"
                                        color={hasDifference ? "error.main" : "success.main"}
                                    >
                                        {hasDifference 
                                            ? `${Math.abs(soldTicketsCount - progressTicketsCount)} tickets`
                                            : "Sin diferencia ✓"
                                        }
                                    </Typography>
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={2} borderTop="1px solid rgba(0,0,0,0.1)">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleRegularizeSoldTickets}
                                    disabled={isRegularizing || !canRegularize}
                                    sx={{ minWidth: 200 }}
                                    title={!canRegularize ? `No hay diferencia entre el progreso (${progressTicketsCount} tickets) y los tickets vendidos (${soldTicketsCount} tickets). No es necesario regularizar.` : `Hay diferencia: Progreso muestra ${progressTicketsCount} tickets, pero hay ${soldTicketsCount} tickets vendidos.`}
                                >
                                    {isRegularizing ? "Regularizando..." : "Regularizar Tickets Vendidos"}
                                </Button>
                                <FormControlLabel
                                    sx={{ ml: 2 }}
                                    control={
                                        <Switch
                                            checked={showOnlyAvailable}
                                            onChange={handleToggleShowOnlyAvailable}
                                            color="primary"
                                        />
                                    }
                                    label="Mostrar solo tickets disponibles"
                                />
                            </Box>
                        </Box>
                    </Alert>
                </Grid>
            )}
            {!showRegularizationPanel && (
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyAvailable}
                                    onChange={handleToggleShowOnlyAvailable}
                                    color="primary"
                                />
                            }
                            label="Mostrar solo tickets disponibles"
                        />
                    </Box>
                </Grid>
            )}
            {talonario && (
                <Grid item xs={12}>
                    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            <Chip size="small" variant="outlined" label="Disponible" />
                            <Chip size="small" label="Seleccionado" sx={{ bgcolor: SELECTED_COLOR, color: getContrastTextColor(SELECTED_COLOR) }} />
                            <Chip size="small" variant="outlined" label="Apartado" sx={{ borderColor: RESERVED_COLOR, color: RESERVED_COLOR, borderWidth: 2 }} />
                            <Chip size="small" label="Pagado" sx={{ bgcolor: colorByAccountId, color: getContrastTextColor(colorByAccountId) }} />
                        </Box>
                        <Button variant="outlined" startIcon={<ImageIcon />} onClick={() => setOpenImageDialog(true)}>
                            Imagen para estados
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Toca los números disponibles para seleccionarlos y luego apártalos o véndelos.
                    </Typography>
                </Grid>
            )}
            {isLoading || isLoadingGetWinner &&  <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>}
            {renderTickets()}

            <Dialog open={openDialogWinner}>
                <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                    <Paper key={winner.email} elevation={3} className="winner-paper" sx={{ width: '100%' }}>
                        <Box p={2} display="flex" alignItems="center">
                            <EmojiEvents fontSize="large" color="primary" />
                            <Box ml={2}>
                                <Typography variant="subtitle1" color="textPrimary">
                                    <Person fontSize="small" color="action" /> {winner.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <Email fontSize="small" color="action" /> {winner.email}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <Phone fontSize="small" color="action" /> {winner.phone}
                                </Typography>
                            </Box>
                        </Box>
                        <Box p={2} display="flex" flexDirection="column" gap={1}>
                            {talonario && winnerTicket !== undefined && (
                                <Chip
                                    size="small"
                                    label={`N° ${formatTicket(winnerTicket)} · ${ticketStates.get(winnerTicket)?.state === 'apartado' ? 'Apartado' : 'Pagado'}`}
                                />
                            )}
                            {talonario && winner?.phone && (
                                <Button size="small" color="success" variant="contained" startIcon={<WhatsAppIcon />} onClick={handleSendWinnerWhatsapp} fullWidth>
                                    Enviar por WhatsApp
                                </Button>
                            )}
                            <Button size="small" onClick={handleCloseWinnerDialog} variant="contained" fullWidth>OK</Button>
                        </Box>
                    </Paper>
                </Box>
            </Dialog>

            {talonario && selectedTickets.length > 0 && (
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        left: 16,
                        right: 16,
                        bottom: isMobile ? 72 : 16,
                        zIndex: 1200,
                        p: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2" sx={{ flex: '1 1 200px' }}>
                        Seleccionados: <strong>{selectedTickets.map(formatTicket).join(', ')}</strong>
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button size="small" onClick={() => setSelectedTickets([])}>Limpiar</Button>
                        <Button size="small" variant="outlined" onClick={() => setSaleMode('apartar')}>Apartar</Button>
                        <Button size="small" variant="contained" onClick={() => setSaleMode('vender')}>Vender</Button>
                    </Box>
                </Paper>
            )}

            {talonario && (
                <>
                    <TalonarioSaleDialog
                        open={!!saleMode}
                        mode={saleMode ?? 'apartar'}
                        raffleId={raffleId}
                        raffleName={raffleName}
                        selectedTickets={selectedTickets}
                        formatTicket={formatTicket}
                        onClose={handleCloseSaleDialog}
                    />
                    <TalonarioImageDialog
                        open={openImageDialog}
                        onClose={() => setOpenImageDialog(false)}
                        limit={limit}
                        takenTickets={getSoldTicketsResponse ?? EMPTY_TICKETS}
                        raffleName={raffleName}
                        formatTicket={formatTicket}
                    />
                </>
            )}

            <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Box mt={2} width={"100%"} display="flex" justifyContent="space-between">
                    {Array.from({ length: totalPages }, (_, i) => (
                        <Button size="small" key={i + 1} onClick={() => setCurrentPage(i + 1)} sx={{ color: currentPage === i + 1 ? "secondary" : "gray" }} variant="text">
                            {i + 1}
                        </Button>
                    ))}
                </Box>
            </Grid>
        </Grid>
    );
};
